import { DI } from '@microsoft/fast-foundation';
import type { Settings as BaseLoginSettings } from '@genesislcap/foundation-login';
import { RESURETY_REMAP_VERSION } from './utils';

export const HostENV = DI.createInterface<string>();

export const HostURL = DI.createInterface<string>();

export type LoginSettings = BaseLoginSettings & {
  autoAuth?: boolean;
};

export interface IState {
  STATE_NAME: string;
  STATE_ABBREVIATION: string;
}

export interface Project {
  AVAILABLE_CAPACITY_AC: number;
  CITY_NAME: string;
  COUNTRY_NAME: string;
  COUNTY_NAME: string;
  CREATED_BY: string;
  HUB_HEIGHT: number;
  INTERCONNECTION_POINT: string;
  INTERCONNECTION_QUEUE_ID: string;
  INTERCONNECTION_VOLTAGE: string;
  INTERCONNECTION_UTILITY: string;
  ISO: string;
  LATITUDE: number;
  LEGAL_NAME: string;
  LONGITUDE: number;
  MEGAWATT_AC: number;
  MEGAWATT_DC: number;
  NAME: string;
  OWNER: string;
  P50_CAPACITY_FACTOR: number;
  PANEL_TECHNOLOGY: string;
  PANEL_TYPE: string;
  PANEL_MAKE_AND_MODEL: string;
  PROJECT_ID: string;
  PROJECT_OPERATIONAL_STATUS: 'NEW_BUILD' | 'OPERATIONAL';
  PROJECT_OPERATOR: string;
  HUB_ZONE_MATRIX_ID: string;
  SETTLEMENT_POINT: string;
  STATE_NAME: string;
  STATE_ABBREVIATION: string;
  TARGET_COD: number | Date;
  GUARANTEED_COD: number | Date;
  COD: number | Date;
  TECHNOLOGY: 'WIND' | 'SOLAR';
  TRACKING_TYPE: string;
  TURBINE_COUNT: number;
  TURBINE_DIAMETER: number;
  TURBINE_MAKE_AND_MODEL: string;
  TURBINE_MEGAWATT: number;
  CREATED_AT: number | Date;
  MODIFIED_BY: string;
  MODIFIED_AT: number | Date;
  PROJECT_STATUS: string;

  // Required fields overwritten by backend
  CASCADE_COMPANY_ID: string;

  // DRAFT SPECIFIC FIELDS
  DRAFT_PROJECT_ID: string;
  TRACKING_TYPE_STRING: string;
  PANEL_TYPE_STRING: string;
  PANEL_TECHNOLOGY_STRING: string;
  ISO_STRING: string;
  INTERCONNECTION_VOLTAGE_STRING: string;
  REMODEL_PROJECT_ID: string;
}

export interface IAllProjects extends Project {
  CASCADE_COMPANY_NAME: string;
}

export interface FieldValidation {
  fieldName: string;
  fieldValue: string;
  fieldHasProblem: boolean;
  errorMessage: string;
  headerName?: string;
}

export interface ObjectValidation {
  objectHasProblem: boolean;
  fields: Array<FieldValidation>;
}

export interface IResponseEvent {
  DETAILS: any;
  IGNORE_WARNINGS: true;
  VALIDATE: false;
}

export interface IHubZoneMatrix {
  HUB_ZONE_MATRIX_ID: string;
  ISO: string;
  SETTLEMENT_POINT: string;
  SETTLEMENT_INDEX: string;
}

export interface IVppaInsert {
  PROJECT_ID: string;
  VPPA_STATUS: string;
  CONTRACT_TYPE: string;
  OFFERED_CAPACITY: number;
  FIXED_PRICE: number;
  TENOR_START_DATE: number | Date;
  TENOR_MONTHS: number;
  SETTLEMENT_POINT: string;
  SETTLEMENT_INDEX: string;
  HUB_ZONE_MATRIX_ID: string;
  REC_DELIVERY_METHODOLOGY?: string;
  PERFORMANCE_GUARANTEE_METHOD: string;
  BUYERS_FRACTION: number;
  VPPA_CREDIT_PROFILE: string;
  SELLER_POST_COD_COLLATERAL: number;
  NEGATIVE_PRICING_METHOD: string;
  APPLICABLE_REC_PROGRAM: string;
  APPLICABLE_TRACKING_SYSTEM?: string;
  VPPA_LEGAL_ENTITY: string;
  VPPA_REFERENCE: string;
  PRODUCT_TYPE: string;
  FLOOR_QUANTITY?: number;
  CASCADE_COMPANY_ID: string;
}

export interface IVppa extends IVppaInsert {
  BUYER_UPSIDE_ALLOCATION: string;
  PROJECT_NAME: string;
  VPPA_ID: string;
  TICKER: string;
  VPPA_STATUS: string;
  CREATED_BY: string;
  CREATED_AT: number | Date;
  MODIFIED_BY: string;
  MODIFIED_AT: number | Date;
  UNIQUE_ID: string;
  NEGOTIATION_STATUS: string;
  NEGOTIATION_ID: string;
  CASCADE_COMPANY_ID: string;
  COMPANY_NAME: string;
  TECHNOLOGY: string;
  AVAILABLE_CAPACITY_AC?: string;
  MEGAWATT_AC?: number;
  TARGET_COD?: number | Date;
  COD?: number | Date;
  GUARANTEED_COD?: number | Date;
  ISO?: string;
  INTERCONNECTION_POINT?: string;
  VPPA_APPLICABLE_REC_PROGRAM?: string;
  VPPA_APPLICABLE_TRACKING_SYSTEM?: string;
  VPPA_REC_DELIVERY_METHODOLOGY?: string;
  PROJECT_OPERATIONAL_STATUS: string;
}

export interface IWatchlist {
  CASCADE_COMPANY_ID?: string;
  WATCHLIST_ENTITY_ID: string;
  WATCHLIST_ENTITY_ID_TYPE: string;
}

export interface ValidationError {
  CODE: string;
  KEY: string;
  KEY_FIELD: any[];
  TABLE: string;
  TEXT: string;
}

export interface Documents {
  BOP_AGREEMENT: string;
  CONSTRUCTION_AGREEMENT: string;
  ENERGY_MANAGEMENT_AGREEMENT: string;
  CULTURAL_RESOURCES_STUDY_REVIEW: string;
  DNH_FROM_FAA: string;
  ENERGY_RESOURCE_STUDY: string;
  EPC_AGREEMENT: string;
  EQUIPMENT_SUPPLY_AGREEMENTS: string;
  ESA_REVIEW: string;
  FLOOD_HAZARD_DETERMINATION: string;
  INDEPENDENT_ENGINEERING_REPORT: string;
  INSURANCE_CONSULTANT_REPORT: string;
  INTERCONNECTION_AGREEMENT: string;
  LAND_CONTROL_AGREEMENTS: string;
  LAND_TITLE_SURVEY: string;
  LITIGATION_DISCLOSURE: string;
  MANAGEMENT_SERVICES_AGREEMENT: string;
  FEASIBILITY_STUDY: string;
  SYSTEM_IMPACT_STUDY: string;
  FACILITIES_STUDY: string;
  OPERATIONS_MAINTENANCE_AGREEMENT: string;
  PRIMARY_COUNTY_LEVEL_PERMIT: string;
  PRIMARY_STATE_LEVEL_PERMIT: string;
  PRIMARY_TOWNSHIP_LEVEL_PERMIT: string;
  PROJECT_ID: string;
  TAX_ABATEMENT_APPLICATION: string;
  THREATENED_ENDANGERED_SPECIES_REVIEW: string;
  TITLE_INSURANCE: string;
  TRANSMISSION_CONGESTION_STUDY: string;
  WETLANDS_DELINEATION_REVIEW: string;
}

export interface IRfqInsert {
  RFQ_STATUS: string;
  TECHNOLOGY: string;
  CONTRACT_TYPE: string;
  COUNTRY_NAME: string;
  STATE_NAME: string;
  CITY_NAME: string;
  TARGET_COD: number | Date;
  GUARANTEED_COD: number | Date;
  REQUESTED_VOLUME: number;
  FIXED_PRICE_LOWER_BOUND: number;
  FIXED_PRICE_UPPER_BOUND: number;
  ISO: string;
  SETTLEMENT_POINT: string;
  SETTLEMENT_INDEX_STRING: string;
  TENOR_START_DATE: number | Date;
  TENOR_MONTHS: number;
  RFQ_NEGATIVE_PRICING_METHOD: string;
  APPLICABLE_TRACKING_SYSTEM: string;
  RFQ_REC_DELIVERY_METHODOLOGY: string;
  RFQ_PERFORMANCE_GUARANTEE_METHOD: string;
  RFQ_APPLICABLE_REC_PROGRAM: string;
  CASCADE_COMPANY_ID: string;
  RFQ_REFERENCE: string;
  EXPECTED_SETTLEMENT_MAX: number;
  EXPECTED_SETTLEMENT_MIN: number;
  DOWNSIDE_RISK_MAX: number;
  DOWNSIDE_RISK_MIN: number;
  NODAL_LME_HISTORIC_MAX: number;
  NODAL_LME_HISTORIC_MIN: number;
  IMPLIED_CARBON_PRICE_MAX: number;
  IMPLIED_CARBON_PRICE_MIN: number;
  CARBON_FREE_ENERGY_UPPER_BOUND: number;
  CARBON_FREE_ENERGY_LOWER_BOUND: number;
  RFQ_CREDIT_PROFILE?: string;
  BUYER_POST_COD_COLLATERAL: number;
  FIXED_PRICE?: number;
  CAPACITY: number;
  PRODUCT_TYPE: string;
  FLOOR_QUANTITY: number;
}

export interface IRfq extends IRfqInsert {
  RFQ_ID: string;
  UNIQUE_ID: string;
  TICKER: string;
  RFQ_STATUS: string;
  CREATED_BY: string;
  CREATED_AT: number | Date;
  MODIFIED_BY: string;
  MODIFIED_AT: number | Date;
  STATE_ABBREVIATION: string;
  LME_RANGE_UPPER_BOUND: number;
  LME_RANGE_LOWER_BOUND: number;
  ESTIMATED_DOWNSIDE_RISK_UPPER_BOUND: number;
  ESTIMATED_DOWNSIDE_RISK_LOWER_BOUND: number;
  CARBON_FREE_ENERGY_UPPER_BOUND: number;
  CARBON_FREE_ENERGY_LOWER_BOUND: number;
  EXPECTED_SETTLEMENT_VALUE_UPPER_BOUND: number;
  EXPECTED_SETTLEMENT_VALUE_LOWER_BOUND: number;
  COMPANY_NAME: string;
  FIXED_PRICE?: number;
  CONTRACT_TYPE: string;
}

export interface IProjectVPPA {
  MODIFIED_AT: number | Date;
  TICKER: string;
  COUNTRY_NAME: string;
  FIXED_PRICE: number;
  ISO: string;
  NAME: string;
  OFFERED_CAPACITY: number;
  PROJECT_ID: string;
  ROW_REF: string;
  SETTLEMENT_POINT: string;
  STATE_NAME: string;
  TARGET_COD: number | Date;
  TECHNOLOGY: 'WIND' | 'SOLAR';
  TENOR_START_DATE: number | Date;
  VPPA_ID: string;
  UNIQUE_ID?: string;
  TENOR_MONTHS?: number;
  PROJECT_OPERATIONAL_STATUS?: string;
  VPPA_STATUS: string;
  VPPA_CREDIT_PROFILE: string;
  SELLER_POST_COD_COLLATERAL: string;
  VPPA_REFERENCE: string;
  IMPLIED_CARBON_PRICE: number;
  APPLICABLE_REC_PROGRAM: string;
  LME: number;
  KEY_METRIC_TIMESTAMP: BigInteger;
  CONTRACT_TYPE: string;
}

export interface IVPPALinkedRFQs {
  MODIFIED_AT: number | Date;
  SELLER_COMPANY_ID: string;
  BUYER_COMPANY_ID: string;
  VPPA_CREDIT_PROFILE: string;
  RFQ_CREDIT_PROFILE: string;
  BUYER_POST_COD_COLLATERAL: string;
  NEGOTIATION_ID: string;
  NEGOTIATION_STATUS: string;
  RFQ_STATUS: string;
  VPPA_ID: string;
  RFQ_ID: string;
  UNIQUE_ID: string;
  TICKER: string;
  TECHNOLOGY: string;
  ISO: string;
  SETTLEMENT_POINT: string;
  TENOR_START_DATE: number | Date;
  TENOR_MONTHS: number;
  VOLUME: number;
  FIXED_PRICE: number;
  IMPLIED_CARBON_PRICE: number;
  APPLICABLE_REC_PROGRAM: string;
  LME: number;
}

export interface VPPALinkedRFQs {
  SELLER_COMPANY_ID: string;
  NEGOTIATION_ID: string;
  NEGOTIATION_STATUS: string;
  PROJECT_ID: string;
  PROJECT_NAME: string;
  VPPA_ID: string;
  RFQ_ID?: string;
  UNIQUE_ID: string;
  RFQ_UNIQUE_ID?: string;
  TICKER: string;
  RFQ_TICKER?: string;
  TECHNOLOGY: string;
  RFQ_TECHNOLOGY?: string;
  ISO: string;
  RFQ_ISO?: string;
  SETTLEMENT_POINT: string;
  RFQ_SETTLEMENT_POINT?: string;
  TENOR_START_DATE: number | Date;
  RFQ_TENOR_START_DATE?: number | Date;
  TENOR_MONTHS: number;
  RFQ_TENOR_MONTHS?: number;
  OFFERED_CAPACITY: number;
  RFQ_VOLUME?: number;
  FIXED_PRICE: number;
}

export interface IRfqLinkedVppas {
  SELLER_COMPANY_ID: string;
  BUYER_COMPANY_ID: string;
  VPPA_ID: string;
  RFQ_ID: string;
  UNIQUE_ID: string;
  NAME: string;
  NEGOTIATION_ID: string;
  NEGOTIATION_STATUS: string;
  TICKER: string;
  FIXED_PRICE: number;
  OFFERED_CAPACITY: number;
  TENOR_MONTHS: number;
  PROJECT_ID: string;
  TECHNOLOGY: string;
  PROJECT: string;
  ISO: string;
  SETTLEMENT_POINT: string;
  VOLUME: number;
  TENOR_START_DATE: number | Date;
  RFQ_APPLICABLE_REC_PROGRAM: string;
  IMPLIED_CARBON_PRICE: number;
  LME: number;
  RFQ_STATUS: string;
  VPPA_STATUS: string;
  MODIFIED_AT: number | Date;
  NEGOTIATION_MODIFIED_AT: number | Date;
  NEGOTIATION_MODIFIED_BY: string;
  PROPOSAL_SENDER: string;
  RFQ_CREDIT_PROFILE: string;
  SELLER_POST_COD_COLLATERAL: number;
  VPPA_CREDIT_PROFILE: string;
}

export interface INegotiationInsert {
  VPPA_ID: string;
  RFQ_ID: string;
  BUYER_COMPANY_ID: string;
  SELLER_COMPANY_ID: string;
  PROJECT_ID: string;
}

export interface INegotiation extends INegotiationInsert {
  PROJECT_ID: string;
  PROJECT_UUID?: string;
  VPPA_UNIQUE_ID: string;
  VPPA_TICKER: string;
  RFQ_UNIQUE_ID: string;
  RFQ_TICKER: string;
  BUYER_COMPANY_ID: string;
  BUYER_COMPANY_NAME: string;
  SELLER_COMPANY_ID: string;
  SELLER_COMPANY_NAME: string;
  CREATED_AT: Date | number;
  CREATED_BY: string;
  MODIFIED_AT: Date | number;
  MODIFIED_BY: string;
  NEGOTIATION_ID: string;
  UNIQUE_ID?: string;
  FIXED_PRICE: number;
  OFFERED_CAPACITY: number;
  VPPA_CREDIT_PROFILE: string;
  RFQ_CREDIT_PROFILE: string;
  SELLER_POST_COD_COLLATERAL: number;
  BUYER_POST_COD_COLLATERAL: number;
  LAST_PROPOSAL_SENDER: string;
  TENOR_MONTHS: number;
  TENOR_START_DATE: number | string;
  ISO: string;
  PROJECT_NAME: string;
  TECHNOLOGY: string;
  NEGOTIATION_STATUS: string;
  RFQ_TECHNOLOGY?: string;
  VPPA_TECHNOLOGY?: string;
  RFQ_TARGET_COD?: Date | number;
  VPPA_TARGET_COD?: Date | number;
  VPPA_COD?: Date | number;
  RFQ_GUARANTEED_COD?: Date | number;
  VPPA_GUARANTEED_COD?: Date | number;
  AVAILABLE_CAPACITY_AC?: string;
  MEGAWATT_AC?: number;
  BUYERS_FRACTION?: number;
  RFQ_TENOR_START_DATE?: number | Date;
  VPPA_TENOR_START_DATE?: number | Date;
  RFQ_TENOR_MONTHS?: number;
  VPPA_TENOR_MONTHS?: number;
  RFQ_FIXED_PRICE?: number;
  VPPA_FIXED_PRICE?: number;
  RFQ_ISO?: string;
  VPPA_ISO?: string;
  RFQ_SETTLEMENT_POINT?: string;
  VPPA_SETTLEMENT_POINT?: string;
  RFQ_SETTLEMENT_INDEX?: string;
  VPPA_SETTLEMENT_INDEX?: string;
  INTERCONNECTION_POINT?: string;
  RFQ_NEGATIVE_PRICING_METHOD?: string;
  VPPA_NEGATIVE_PRICING_METHOD?: string;
  PROJECT_OPERATIONAL_STATUS: string;
  RFQ_APPLICABLE_REC_PROGRAM?: string;
  VPPA_APPLICABLE_REC_PROGRAM?: string;
  VPPA_REC_DELIVERY_METHODOLOGY?: string;
  RFQ_REC_DELIVERY_METHODOLOGY?: string;
  VPPA_APPLICABLE_TRACKING_SYSTEM?: string;
  RFQ_APPLICABLE_TRACKING_SYSTEM?: string;
  RFQ_PERFORMANCE_GUARANTEE_METHOD?: string;
  VPPA_PERFORMANCE_GUARANTEE_METHOD?: string;
  NAME?: string;
  REPORTING_COUNTERPARTY?: string;
  BUYER_VISIBILITY: boolean;
  SELLER_VISIBILITY: boolean;
  VPPA_CONTRACT_TYPE?: string;
  RFQ_CONTRACT_TYPE?: string;
  EFFECTIVE_DATE?: number;
}

export interface IProposalInsert {
  FIXED_PRICE: number;
  OFFERED_CAPACITY: number;
  NEGOTIATION_ID: string;
  SELLER_POST_COD_COLLATERAL: number;
  BUYER_POST_COD_COLLATERAL: number;
  TENOR_MONTHS: number;
  TENOR_START_DATE: string | number;
}

export interface IProposal extends IProposalInsert {
  CREATED_AT: Date | number;
  CREATED_BY: string;
  PROPOSAL_SENDER: string;
  SELLER_COMPANY_ID: string;
  SELLER_COMPANY_NAME: string;
  BUYER_COMPANY_ID: string;
  BUYER_COMPANY_NAME: string;
  PROPOSAL_ID: string;
  RFQ_CREDIT_PROFILE: string;
  VPPA_CREDIT_PROFILE: string;
}
export interface INegotiationSummary {
  VPPA_ID: string;
  VPPA_UNIQUE_ID: string;
  RFQ_UNIQUE_ID: string;
  VPPA_TICKER: string;
  RFQ_TICKER: string;
  BUYER_COMPANY_NAME: string;
  SELLER_COMPANY_NAME: string;
  NEGOTIATION_ID: string;
  REPORTING_COUNTERPARTY?: string;
  CONTRACT_SYMBOL_VPPA: string;
  CONTRACT_SYMBOL_RFQ: string;
  BUYER: string;
  SELLER: string;
  NAME: string; // for project name
  VPPA_TECHNOLOGY: string;
  RFQ_TECHNOLOGY: string;
  VPPA_TARGET_COD: number | Date;
  VPPA_COD: number | Date;
  RFQ_TARGET_COD: number | Date;
  VPPA_GUARANTEED_COD: number | Date;
  RFQ_GUARANTEED_COD: number | Date;
  MEGAWATT_AC: number; // for nameplate capacity
  OFFERED_CAPACITY: number;
  BUYERS_FRACTION: number;
  VPPA_TENOR_START_DATE: number | Date;
  RFQ_TENOR_START_DATE: number | Date;
  VPPA_TENOR_MONTHS: number;
  RFQ_TENOR_MONTHS: number;
  VPPA_FIXED_PRICE: number;
  RFQ_FIXED_PRICE: number;
  VPPA_ISO: string;
  RFQ_ISO: string;
  VPPA_SETTLEMENT_POINT: string;
  RFQ_SETTLEMENT_POINT: string;
  VPPA_SETTLEMENT_INDEX: string;
  RFQ_SETTLEMENT_INDEX: string;
  INTERCONNECTION_POINT: string;
  VPPA_NEGATIVE_PRICING_METHOD: string;
  RFQ_NEGATIVE_PRICING_METHOD: string;
  VPPA_CREDIT_PROFILE: string;
  RFQ_CREDIT_PROFILE: string;
  SELLER_POST_COD_COLLATERAL: number;
  BUYER_POST_COD_COLLATERAL: number;
  VPPA_APPLICABLE_REC_PROGRAM: string;
  RFQ_APPLICABLE_REC_PROGRAM: string;
  VPPA_APPLICABLE_TRACKING_SYSTEM: string;
  RFQ_APPLICABLE_TRACKING_SYSTEM: string;
  VPPA_REC_DELIVERY_METHODOLOGY: string;
  RFQ_REC_DELIVERY_METHODOLOGY: string;
  VPPA_PERFORMANCE_GUARANTEE_METHOD: string;
  RFQ_PERFORMANCE_GUARANTEE_METHOD: string;
  PROJECT_OPERATIONAL_STATUS: string;
  VPPA_CONTRACT_TYPE: string;
  RFQ_CONTRACT_TYPE: string;
}

export interface WatchlistProject {
  PROJECT_NAME: string;
  PROJECT_OPERATIONAL_STATUS: string;
  COMPANY_NAME: string;
  TECHNOLOGY: 'WIND' | 'SOLAR' | 'BOTH';
  AVAILABLE_CAPACITY_AC: number;
  MEGAWATT_AC: number;
  ISO: string;
  WATCHLIST_ID: string;
  WATCHLIST_ENTITY_ID: string;
  WATCHLIST_ENTITY_ID_TYPE: 'PROJECT' | 'VPPA';
}

export interface WatchlistVPPA {
  TICKER: string;
  NAME: string;
  TECHNOLOGY: 'WIND' | 'SOLAR' | 'BOTH';
  OFFERED_CAPACITY: number;
  ISO: string;
  WATCHLIST_ID: string;
  WATCHLIST_ENTITY_ID: string;
  WATCHLIST_ENTITY_ID_TYPE: 'PROJECT' | 'VPPA';
}

export interface ICompany {
  NAME: string;
  PHONE_NUMBER: string;
  ADDRESS_STREET: string;
  ADDRESS_ZIP_CODE: string;
  BILLING_INFORMATION: string;
  CASCADE_COMPANY_ID: string;
  CASCADE_PARENT_COMPANY_ID: string;
  CITY_NAME: string;
  STATE_NAME: string;
  CONSUMER: string;
  COUNTRY_NAME: string;
  CREDITWORTHY_INSTITUTION?: boolean;
  CREDIT_PROFILE: string;
  CULTURAL_CONSIDERATIONS: string;
  ECONOMICALLY_DISADVANTAGED: boolean;
  INDUSTRY_TYPE: string;
  INDUSTRY_CATEGORY: string;
  PARENT_GUARANTEE_FROM_CREDITHWORTHY_INSTITUTION?: boolean;
  COMPANY_ROLE: string;
  SEF_PARTICIPANT_AGREEMENT_CONSENT: boolean;
  SEF_PARTICIPANT_AGREEMENT_CONSENT_DATETIME: number;
  NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE: number;
  REGULATORY_STATUS: string;
  COMPANY_LEI: string;
  FINANCIAL_ENTITY_INDICATOR: boolean;
  FEDERAL_ENTITY_INDICATOR: boolean;
  SDR_TRADE_ALLOCATION_INDICATOR: string;
  POST_LETTERS_OF_CREDIT?: boolean;
  RFQ_NEGOTIATION_FLAG: boolean;
  AUTO_EXECUTION_FLAG: boolean;
  MANUAL_REGULATORY_STATUS: string;
  IS_SPV_FLAG: boolean;
}

export interface ICompanyForm {
  CASCADE_COMPANY_ID: string;
  NAME: string;
  PHONE_NUMBER: number;
  ADDRESS_STREET: string;
  ADDRESS_ZIP_CODE: string;
  CITY_NAME: string;
  STATE_NAME: string;
  CREDITWORTHY_INSTITUTION: boolean;
  PARENT_GUARANTEE_FROM_CREDITHWORTHY_INSTITUTION: boolean;
  COUNTRY_NAME: string;
  CREDIT_PROFILE: string;
  COMPANY_ROLE: string;
  NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE: number;
  REGULATORY_STATUS: string;
  COMPANY_LEI: string;
  FINANCIAL_ENTITY_INDICATOR: boolean;
  FEDERAL_ENTITY_INDICATOR: boolean;
  POST_LETTERS_OF_CREDIT: boolean;
  RFQ_NEGOTIATION_FLAG: boolean;
  AUTO_EXECUTION_FLAG: boolean;
  MANUAL_REGULATORY_STATUS: string;
  IS_SPV_FLAG: boolean;
  CASCADE_PARENT_COMPANY_ID: string;
}

export interface IUserCompany {
  USER_NAME: string;
  CASCADE_COMPANY_ID: string;
  CASCADE_COMPANY_NAME: string;
}

// Interface for Negotiation details(top right section of Structured Chat)
export interface NegotiationDetails {
  PROJECT_ID: string;
  PROJECT_NAME: string;
  VPPA_TICKER: string;
  VPPA_UNIQUE_ID: string;
  VPPA_FIXED_PRICE: number;
  VPPA_OFFERED_CAPACITY: number;
  VPPA_VOLUME: number;
  VPPA_TENOR_START_DATE: Date | number;
  VPPA_TENOR_MONTHS: number;
  RFQ_CREDIT_PROFILE: string;
  VPPA_CREDIT_PROFILE: string;
  VPPA_COLLATERAL: number;
  RFQ_TICKER: string;
  RFQ_UNIQUE_ID: string;
  RFQ_FIXED_PRICE: string;
  RFQ_OFFERED_CAPACITY: string;
  RFQ_VOLUME: number;
  RFQ_TENOR_START_DATE: Date | number;
  RFQ_COLLATERAL: number;
  RFQ_TENOR_MONTHS: number;
  NEGOTIATION_STATUS_ENUM_VALUE: string;
  NEGOTIATION_STATUS: string;
  NEGOTIATION_STATUS_CLASS: string;
  COMPANY_NAME: string;
}

// Object that needs to be passed when opening the Structured Chat
export interface ISCSelectedEntities {
  selectedVPPAIDs: string[];
  selectedRFQIDs: string[];
  selectedNegotiationID: string;
}

export interface IProjectDocumentStatus extends Project {
  APPLICABLE_REC_PROGRAM: string;
  AVAILABLE_CAPACITY_AC: number;
  BOP_AGREEMENT: string;
  CASCADE_COMPANY_ID: string;
  CITY_NAME: string;
  CONSTRUCTION_AGREEMENT: string;
  ENERGY_MANAGEMENT_AGREEMENT: string;
  COUNTRY_NAME: string;
  COUNTY_NAME: string;
  CREATED_AT: number;
  CREATED_BY: string;
  CULTURAL_RESOURCES_STUDY_REVIEW: string;
  DNH_FROM_FAA: string;
  ENERGY_RESOURCE_STUDY: string;
  EPC_AGREEMENT: string;
  EQUIPMENT_SUPPLY_AGREEMENTS: string;
  ESA_REVIEW: string;
  FLOOD_HAZARD_DETERMINATION: string;
  GUARANTEED_COD: number;
  COD: number;
  HUB_ZONE_MATRIX_ID: string;
  INDEPENDENT_ENGINEERING_REPORT: string;
  INSURANCE_CONSULTANT_REPORT: string;
  INTERCONNECTION_AGREEMENT: string;
  INTERCONNECTION_POINT: string;
  INTERCONNECTION_QUEUE_ID: string;
  INTERCONNECTION_VOLTAGE: string;
  ISO: string;
  LAND_CONTROL_AGREEMENTS: string;
  LAND_TITLE_SURVEY: string;
  LATITUDE: number;
  LEGAL_NAME: string;
  LITIGATION_DISCLOSURE: string;
  LONGITUDE: number;
  MANAGEMENT_SERVICES_AGREEMENT: string;
  FEASIBILITY_STUDY: string;
  SYSTEM_IMPACT_STUDY: string;
  FACILITIES_STUDY: string;
  MEGAWATT_AC: number;
  MEGAWATT_DC: number;
  MODIFIED_AT: number;
  MODIFIED_BY: string;
  NAME: string;
  OPERATIONS_MAINTENANCE_AGREEMENT: string;
  OWNER: string;
  P50_CAPACITY_FACTOR: number;
  PANEL_MAKE_AND_MODEL: string;
  PANEL_TECHNOLOGY: string;
  PANEL_TYPE: string;
  PRIMARY_COUNTY_LEVEL_PERMIT: string;
  PRIMARY_STATE_LEVEL_PERMIT: string;
  PRIMARY_TOWNSHIP_LEVEL_PERMIT: string;
  PROJECT_ID: string;
  PROJECT_OPERATOR: string;
  PROJECT_STATUS: string;
  RECORD_ID: number;
  SETTLEMENT_INDEX: string;
  SETTLEMENT_POINT: string;
  STATE_ABBREVIATION: string;
  STATE_NAME: string;
  TARGET_COD: number;
  TAX_ABATEMENT_APPLICATION: string;
  THREATENED_ENDANGERED_SPECIES_REVIEW: string;
  TIMESTAMP: number;
  TITLE_INSURANCE: string;
  TRACKING_TYPE: string;
  TRANSMISSION_CONGESTION_STUDY: string;
  TURBINE_DIAMETER: number;
  TURBINE_MAKE_AND_MODEL: string;
  TURBINE_MEGAWATT: number;
  WETLANDS_DELINEATION_REVIEW: string;
  CASCADE_COMPANY_NAME: string;
  APPLICABLE_TRACKING_SYSTEM: string;
}

export interface IProjectHistoricalTs {
  PROJECT_ID: string;
  VPPA_ID: string;
  METRICS_DATETIME: number;
  HOURS: number;
  GENERATION_MWH: number;
  LOAD_MWH: number;
  PRICE_ATC_HUB_USD_PER_MWH: number;
  PRICE_ATC_NODE_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_USD_PER_MWH: number;
  PRICE_ASGEN_NODE_USD_PER_MWH: number;
  LME_ATC_NODE_MT_CO2E_PER_MWH: number;
  LME_ASGEN_NODE_MT_CO2E_PER_MWH: number;
  CFE_PERCENT: number;
  SETTLEMENT_USD_PER_MWH: number;
  SETTLEMENT_USD: number;
  BASIS_ATC_USD_PER_MWH: number;
  BASIS_ASGEN_USD_PER_MWH: number;
  BASIS_SUM_USD: number;
  BASIS_CUMULATIVE_SUM_USD: number;
  CARBON_ABATED_NODE_MT_CO2E: number;
}

export interface IProjectForecastTs {
  PROJECT_ID: string;
  VPPA_ID: string;
  METRICS_DATETIME: number;
  HOURS: number;
  GENERATION_MEAN_MWH: number;
  LME_ATC_HUB_MT_CO2E_PER_MWH: number;
  PRICE_ATC_HUB_BASELINE_MEAN_USD_PER_MWH: number;
  PRICE_ATC_HUB_BASELINE_P05_USD_PER_MWH: number;
  PRICE_ATC_HUB_BASELINE_P95_USD_PER_MWH: number;
  PRICE_ATC_HUB_HIGH_GAS_MEAN_USD_PER_MWH: number;
  PRICE_ATC_HUB_HIGH_GAS_P05_USD_PER_MWH: number;
  PRICE_ATC_HUB_HIGH_GAS_P95_USD_PER_MWH: number;
  PRICE_ATC_HUB_LOW_GAS_MEAN_USD_PER_MWH: number;
  PRICE_ATC_HUB_LOW_GAS_P05_USD_PER_MWH: number;
  PRICE_ATC_HUB_LOW_GAS_P95_USD_PER_MWH: number;
  SETTLEMENT_BASELINE_MEAN_SUM_USD: number;
  SETTLEMENT_BASELINE_P05_SUM_USD: number;
  SETTLEMENT_BASELINE_P95_SUM_USD: number;
  SETTLEMENT_BASELINE_MEAN_USD_PER_MWH: number;
  SETTLEMENT_BASELINE_P05_USD_PER_MWH: number;
  SETTLEMENT_BASELINE_P95_USD_PER_MWH: number;
  LME_ASGEN_HUB_MT_CO2E_PER_MWH: number;
  PRICE_ASGEN_HUB_BASELINE_MEAN_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_BASELINE_P05_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_BASELINE_P95_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_HIGH_GAS_MEAN_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_HIGH_GAS_P05_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_HIGH_GAS_P95_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_LOW_GAS_MEAN_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_LOW_GAS_P05_USD_PER_MWH: number;
  PRICE_ASGEN_HUB_LOW_GAS_P95_USD_PER_MWH: number;
  SETTLEMENT_HIGH_GAS_MEAN_SUM_USD: number;
  SETTLEMENT_HIGH_GAS_P05_SUM_USD: number;
  SETTLEMENT_HIGH_GAS_P95_SUM_USD: number;
  SETTLEMENT_LOW_GAS_MEAN_SUM_USD: number;
  SETTLEMENT_LOW_GAS_P05_SUM_USD: number;
  SETTLEMENT_LOW_GAS_P95_SUM_USD: number;
  SETTLEMENT_HIGH_GAS_MEAN_USD_PER_MWH: number;
  SETTLEMENT_HIGH_GAS_P05_USD_PER_MWH: number;
  SETTLEMENT_HIGH_GAS_P95_USD_PER_MWH: number;
  SETTLEMENT_LOW_GAS_MEAN_USD_PER_MWH: number;
  SETTLEMENT_LOW_GAS_P05_USD_PER_MWH: number;
  SETTLEMENT_LOW_GAS_P95_USD_PER_MWH: number;
  SETTLEMENT_BASELINE_MEAN_NPV_USD: number;
  SETTLEMENT_BASELINE_P05_NPV_USD: number;
  SETTLEMENT_BASELINE_P95_NPV_USD: number;
  SETTLEMENT_HIGH_GAS_MEAN_NPV_USD: number;
  SETTLEMENT_HIGH_GAS_P05_NPV_USD: number;
  SETTLEMENT_HIGH_GAS_P95_NPV_USD: number;
  SETTLEMENT_LOW_GAS_MEAN_NPV_USD: number;
  SETTLEMENT_LOW_GAS_P05_NPV_USD: number;
  SETTLEMENT_LOW_GAS_P95_NPV_USD: number;
  SETTLEMENT_CUMULATIVE_BASELINE_MEAN_USD: number;
  SETTLEMENT_CUMULATIVE_BASELINE_P05_USD: number;
  SETTLEMENT_CUMULATIVE_BASELINE_P95_USD: number;
  SETTLEMENT_CUMULATIVE_HIGH_GAS_MEAN_USD: number;
  SETTLEMENT_CUMULATIVE_HIGH_GAS_P05_USD: number;
  SETTLEMENT_CUMULATIVE_HIGH_GAS_P95_USD: number;
  SETTLEMENT_CUMULATIVE_LOW_GAS_MEAN_USD: number;
  SETTLEMENT_CUMULATIVE_LOW_GAS_P05_USD: number;
  SETTLEMENT_CUMULATIVE_LOW_GAS_P95_USD: number;
  SETTLEMENT_ICP_BASELINE_MEAN_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_BASELINE_P05_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_BASELINE_P95_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_HIGH_GAS_MEAN_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_HIGH_GAS_P05_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_HIGH_GAS_P95_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_LOW_GAS_MEAN_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_LOW_GAS_P05_USD_PER_MT_CO2E: number;
  SETTLEMENT_ICP_LOW_GAS_P95_USD_PER_MT_CO2E: number;
  CARBON_ABATED_EXPECTED_MT_CO2E: number;
}

export interface IArteriaNegotiation {
  BUYER_COMPANY_ID: string;
  SELLER_COMPANY_ID: string;
  NEGOTIATION_STATUS: string;
  TICKER: string;
  VPPA_ID: string;
  VPPA_UNIQUE_ID: string;
  RFQ_ID: string;
  RFQ_UNIQUE_ID: string;
  UPI?: string;
  UNIQUE_TRANSACTION_IDENTIFIER?: string;
  NEGOTIATION_ID: string;
  UNIQUE_ID: string;
  FORM_AGREEMENT_TYPE: string;
  CONTRACT_TYPE: string;
  BUYER_COMPANY_NAME: string;
  SELLER_COMPANY_NAME: string;
  NAME: string;
  TECHNOLOGY: string;
  STATE_NAME: string;
  ISO: string;
  SETTLEMENT_POINT: string;
  SETTLEMENT_INDEX: string;
  MEGAWATT_AC: string;
  BUYERS_FRACTION: string;
  TENOR_START_DATE: string;
  TENOR_MONTHS: string;
  ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS: string;
  FIXED_PRICE: string;
  UPSIDE_SHARING_FIXED_PRICE: string;
  SELLER_ALLOCATED_UPSIDE_PERCENTAGE: string;
  BUYER_UPSIDE_ALLOCATION_PRICE: string;
  BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE: string;
  COLLAR_CAP_PRICE: string;
  COLLAR_FLOOR_PRICE: string;
  NEGATIVE_PRICING_METHOD?: string
  APPLICABLE_REC_PROGRAM: string;
  REC_DELIVERY_METHODOLOGY: string;
  APPLICABLE_TRACKING_SYSTEM: string
  PERFORMANCE_GUARANTEE_METHOD: string;
  NOTIONAL_ROUNDED?: number;
  REPORTING_COUNTERPARTY?: string;
  EXECUTION_FIXED_PRICE?: number;
  EXECUTION_TENOR_START_DATE?: number;
  EXECUTION_TENOR_MONTHS?: number;
  EXECUTION_NAMEPLATE_CAPACITY?: number;
  EXECUTION_BUYERS_FRACTION?: number;
}

export interface IUserCompanyListUpdateRequest {
  SIDE_SELECTED: string;
  COMPANIES_LIST: Array<string>;
  USERS_LIST: Array<string>;
}

export interface ICompanyUsersRequest {
  CASCADE_COMPANY_ID: string;
  USER_NAMES: Array<string>;
}

export interface IAdvisorUserCompanyUpdateRequest {
  BUYER_COMPANY_ID: string;
  USERS: IAdvisorUserCompanyListUpdateRequest[];
}

export interface IAdvisorUserCompanyListUpdateRequest {
  USER_NAME: string;
  ASSIGNED: boolean;
}

export enum GOVERNING_AGREEMENT {
  EXISTING_ISDA_MASTER_AGREEMENT = 'EXISTING_ISDA_MASTER_AGREEMENT',
  STANDALONE_VPPA = 'STANDALONE_VPPA',
}

export enum AVERAGE_TYPE {
  ALL_ISOS = 'ALL_ISOS',
  SINGLE_ISO = 'SINGLE_ISO',
}

export interface ICompanyApproval {
  IS_CHECKED: boolean;
  CASCADE_COMPANY_ID: string;
  CASCADE_COMPANY_NAME: string;
  COUNTERPARTY_COMPANY_ID: string;
  GOVERNING_AGREEMENT: GOVERNING_AGREEMENT;
  EFFECTIVE_DATE: number | Date;
}

export interface IExecutionNegotiation extends INegotiation {
  UPI?: string;
  UNIQUE_TRANSACTION_IDENTIFIER?: string;
  APPLICABLE_REC_PROGRAM?: string;
  APPLICABLE_TRACKING_SYSTEM?: string;
  REC_DELIVERY_METHODOLOGY?: string;
  COUNTRY_NAME?: string;
  COUNTY_NAME?: string;
  FORM_AGREEMENT_TYPE?: string;
  GOVERNING_AGREEMENT?: string;
  MAKE_AND_MODEL?: string;
  RFQ_CONTRACT_TYPE?: string;
  SETTLEMENT_INDEX?: string;
  SETTLEMENT_POINT?: string;
  STATE_NAME?: string;
  VPPA_CONTRACT_TYPE?: string;
  INTERCONNECTION_UTILITY: string;
}

export interface IProjectMetrics {
  PROJECT_ID: string;
  PROJECT_METRICS: Array<IVppaMetrics>;
}

export interface IVppaMetrics {
  VPPA_ID: string;
  ALL_ISO_METRICS: Array<IMetric>;
  SINGLE_ISO_METRICS: Array<IMetric>;
}

export interface IMetric {
  AVERAGE: number;
  FIELD_NAME: string;
  RANKING: number;
  READABLE_FIELD_NAME: string;
  VALUE: number;
}

export interface AnalyticsPreferences {
  PRODUCT_TYPE: string;
  PREFERRED_LOCATION: string;
  LOCATION: string;
  EXPECTED_SETTLEMENT: string;
  LME: string;
  IMPLIED_CARBON_PRICE: string;
  PROJECT_ADDITIONALITY: string;
  APPLICABLE_REC_PROGRAM?: string;
}

export interface VppaRankings {
  APPLICABLE_REC_PROGRAM: string;
  BUYERS_FRACTION: number;
  CONTRACT_TYPE: string;
  COUNTY: string;
  EXPECTED_SETTLEMENT: number;
  FIXED_PRICE: number;
  FORM_AGREEMENT_TYPE: string;
  IMPLIED_CARBON_PRICE: number;
  ISO: string;
  LME: number;
  LOCATION: number;
  NAMEPLATE_CAPACITY: number;
  OFFERED_CAPACITY: number;
  PERFORMANCE_GUARANTEE_METHOD: string;
  PROJECT_ADDITIONALITY: number;
  PROJECT_ID: string;
  PROJECT_NAME: string;
  RFQ_UNIQUE_ID: string;
  SCORE: number;
  SELLER: string;
  SELLER_POST_COD_COLLATERAL: string;
  SETTLEMENT_INDEX: string;
  SETTLEMENT_POINT: string;
  STATE: string;
  SWAP_SETTLEMENT_CALCULATION_METHODS: string;
  TECHNOLOGY: string;
  TENOR_MONTHS: number;
  TENOR_START_DATE: number;
  TICKER: string;
  UNIQUE_ID: string;
  VPPA_ID: string;
  VPPA_STATUS: string;
  REFERENCED_EXPECTED_SETTLEMENT: number;
  REFERENCED_IMPLIED_CARBON_PRICE: number;
  REFERENCED_LME: number;
  PROJECT_OPERATIONAL_STATUS: string;
}

export interface IVppaRanking {
  BASIS_ASGEN_3_YEAR_USD_PER_MWH_Q: number;
  CFE_3_YEAR_PERCENT_Q: number;
  EXPECTED_SETTLEMENT_CONTRACT_DATES_USD_PER_MWH_Q: number;
  PROJECT_ID: string;
  RANKING_TYPE: string;
  VPPA_ID: string;
}

export interface IVppaAverages {
  BASIS_ASGEN_3_YEAR_USD_PER_MWH_AVG: number;
  CFE_3_YEAR_PERCENT_AVG: number;
  EXPECTED_SETTLEMENT_CONTRACT_DATES_USD_PER_MWH_AVG: number;
  ISO_FILTER: string;
  TECHNOLOGY_FILTER: string;
}

export interface DisplayMetrics {
  readableName: string;
  fieldName: string;
  vppaValue: string;
  averageFieldName: string;
  averageValue: string;
  rankingValue: string;
  rankingFieldName: string;
}

export interface ThirdPartyIntegration {
  FIRST_NAME?: string;
  LAST_NAME?: string;
  RESURETY_REMAP_VERSION: RESURETY_REMAP_VERSION;
  USER_NAME: string;
  USER_UUID?: string;
}

export interface RESURETY_REMODEL_INTERCONNECTION_POINT {
  RESURETY_LOCATION_ID: string;
  ISO: string;
  NAME: string;
  FIRST_PRICE_DATE: number | Date;
}

export interface ValidProfiles {
  PROFILE_1: string;
  PROFILE_2: string;
}

export interface DashboardMetric {
  PROJECT_ID: string;
  REMAINING_VOLUME: number;
  EXECUTED_VOLUME: number;
  CASCADE_COMPANY_ID: string;
}

export interface IValidProfileByCompanyRole {
  COMPANY_ROLE: string;
  PROFILE_NAME: string;
}
